import React from 'react';
import { NavLink, useParams } from 'react-router-dom';
import { Header } from './Header';
import { useOrganization } from '../hooks/useOrganization';
import { Unauthorized } from '../pages/Unauthorized';

interface OrganizationAdminLayoutProps {
  children: React.ReactNode;
  title: string;
}

export const OrganizationAdminLayout: React.FC<OrganizationAdminLayoutProps> = ({ children, title }) => {
  const { organization, isLoading, isAdmin } = useOrganization();
  const { orgSlug } = useParams<{ orgSlug: string }>();

  if (isLoading) {
    return (
      <div className="app-container">
        <Header />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
          Loading organization...
        </div>
      </div>
    );
  }

  if (!organization || !isAdmin) {
    return <Unauthorized message="You do not have administrative access to this organization." />;
  }

  const navItems = [
    { label: 'Workspaces', path: `/org/${orgSlug}/admin/workspaces` },
    { label: 'Members', path: `/org/${orgSlug}/admin/members` },
    { label: 'Billing', path: `/org/${orgSlug}/admin/billing` },
    { label: 'Settings', path: `/org/${orgSlug}/admin/settings`, ownerOnly: true },
  ];

  return (
    <div className="app-container flex flex-col h-screen bg-background">
      <Header>
        <div className="ml-4 pl-4 border-l border-border text-muted-foreground font-medium hidden sm:block">
          {organization.name} <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded ml-1 uppercase tracking-wider">Org Admin</span>
        </div>
      </Header>
      
      <div className="flex flex-1 overflow-hidden">
        <aside className="w-64 border-r border-border bg-card hidden md:flex flex-col py-6">
          <nav className="flex-1">
            <ul className="space-y-1 px-3">
              {navItems.map((item) => (
                (!item.ownerOnly || organization.role === 'owner') && (
                  <li key={item.path}>
                    <NavLink 
                      to={item.path}
                      className={({ isActive }) => `
                        flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all
                        ${isActive 
                          ? 'bg-primary text-primary-foreground shadow-sm' 
                          : 'text-muted-foreground hover:bg-accent hover:text-foreground'}
                      `}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                )
              ))}
            </ul>
          </nav>
          
          <div className="px-6 pt-6 border-t border-border mt-auto">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-xs">
                {organization.name[0].toUpperCase()}
              </div>
              <div className="flex flex-col overflow-hidden">
                <span className="text-xs font-bold truncate">{organization.name}</span>
                <span className="text-[10px] text-muted-foreground capitalize">{organization.role}</span>
              </div>
            </div>
          </div>
        </aside>

        <main className="flex-1 overflow-y-auto bg-muted/20">
          <div className="max-w-6xl mx-auto p-8 lg:p-12">
            <header className="mb-10">
              <h2 className="text-3xl font-extrabold tracking-tight">{title}</h2>
              <div className="h-1 w-12 bg-primary mt-2 rounded-full"></div>
            </header>
            <div className="animate-in fade-in slide-in-from-bottom-2 duration-500">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};



